import { useMemo, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Textarea } from '@/components/form/Textarea';
import { ymd } from '@/components/ui/PeriodPicker';
import { CONTRA_MODES, type LedgerOption, type VoucherInput } from '@/api/accountsBooks';
import { ChipTag, DateField, F, FormFooter, FormTitle, Msgs, Posting, ReadBox, Sec, SmBtn } from './voucherFormParts';
import { cashBank, curBal, dd, inr, r2 } from './voucherFormat';
import { useSaveVoucher, type FormProps } from './useVoucherForm';

/* Contra — money moved between your own cash and bank accounts, as the prototype:
   Account (money out · Cr) → Particulars (one or more accounts, money in · Dr)
   → Bank Allocation (when a bank is involved) → Narration → Posting. */

interface Row { key: number; to: number | ''; amount: number }
interface State { date: string; from: number | ''; rows: Row[]; mode: string; ref: string; idate: string; narr: string }
let seq = 1;

/** The narration a contra gets when none is typed. */
function ctNarr(from?: LedgerOption, to: (LedgerOption | undefined)[] = []) {
  if (from?.kind !== 'cash' && to.length && to.every((x) => x?.kind === 'cash')) return 'Cash withdrawn for petty expenses';
  if (from?.kind === 'cash' && to.length && to.every((x) => x?.kind === 'bank')) return 'Cash deposited in bank';
  return 'Fund transfer between own accounts';
}

export function ContraForm({ voucher, ledgers, nextNo, onClose }: FormProps) {
  const byId = useMemo(() => new Map(ledgers.map((l) => [l.id, l])), [ledgers]);
  const accts = ledgers.filter(cashBank);
  const today = ymd(new Date());

  const [s, setS] = useState<State>(() => {
    if (!voucher) {
      const from = accts.find((a) => a.kind === 'bank') ?? accts[0];
      const to = accts.find((a) => a.kind === 'cash' && a.id !== from?.id) ?? accts.find((a) => a.id !== from?.id);
      return { date: today, from: from?.id ?? ('' as number | ''), rows: [{ key: seq++, to: to?.id ?? ('' as number | ''), amount: 0 }], mode: from?.kind === 'cash' ? 'Cash Deposit' : 'Cheque', ref: '', idate: today, narr: '' };
    }
    const fromLine = voucher.lines.find((l) => l.side === 'Cr');
    return {
      date: voucher.date, from: fromLine?.ledgerId ?? '', mode: voucher.mode && voucher.mode !== 'Cash' ? voucher.mode : 'Cheque',
      ref: voucher.refNo ?? '', idate: voucher.instrDate ?? voucher.date, narr: voucher.narration ?? '',
      rows: voucher.lines.filter((l) => l !== fromLine).map((l) => ({ key: seq++, to: l.ledgerId, amount: l.amount })),
    };
  });
  const set = (p: Partial<State>) => setS((x) => ({ ...x, ...p }));
  const setRow = (i: number, p: Partial<Row>) => setS((x) => ({ ...x, rows: x.rows.map((r, j) => (j === i ? { ...r, ...p } : r)) }));

  const from = s.from === '' ? undefined : byId.get(s.from);
  const tos = s.rows.map((r) => (r.to === '' ? undefined : byId.get(r.to)));
  const total = r2(s.rows.reduce((t, r) => t + r.amount, 0));
  const bankSide = from?.kind === 'bank' || tos.some((x) => x?.kind === 'bank');

  /** A new "from" account: rows can't take it, and the transaction type follows it. */
  const setFrom = (id: number) => {
    const other = accts.find((a) => a.id !== id)?.id ?? '';
    set({ from: id, rows: s.rows.map((r) => (r.to === id ? { ...r, to: other } : r)), mode: byId.get(id)?.kind === 'cash' ? 'Cash Deposit' : 'Cheque' });
  };
  const addRow = () => {
    const used = new Set([s.from, ...s.rows.map((r) => r.to)]);
    const a = accts.find((x) => !used.has(x.id)) ?? accts.find((x) => x.id !== s.from);
    set({ rows: [...s.rows, { key: seq++, to: a?.id ?? '', amount: 0 }] });
  };

  const block: string[] = [];
  if (!s.date) block.push('Enter the date.');
  else if (s.date > today) block.push(`The date can’t be after today (${dd(today)}).`);
  if (!from) block.push('Choose the account the money goes out of.');
  if (!(total > 0)) block.push('Enter the amount.');
  if (s.rows.some((r) => r.to !== '' && r.to === s.from)) block.push('Money cannot go from an account into the same account.');
  const chosen = s.rows.map((r) => r.to).filter((x) => x !== '');
  if (new Set(chosen).size < chosen.length) block.push('The same account is entered twice — put it on one line.');
  if (s.rows.some((r) => r.to === '' && r.amount > 0)) block.push('Choose the account on every line.');
  if (bankSide && !s.idate) block.push('Bank Allocation: enter the instrument date.');
  else if (bankSide && s.mode !== 'Cheque' && s.idate > s.date) block.push(`Bank Allocation: the ${s.mode} date can't be after the voucher date (${dd(s.date)}).`);
  if (bankSide && s.mode !== 'Cash Deposit' && !s.ref.trim()) block.push(`Bank Allocation: enter the ${s.mode === 'Cheque' ? 'cheque' : 'UTR / reference'} number.`);

  const post = [...s.rows.map((r, i) => ({ l: tos[i]?.name ?? '', a: r.amount })), { l: from?.name ?? '', a: -total }];
  const build = (): VoucherInput => ({
    date: s.date,
    narration: s.narr.trim() || ctNarr(from, tos),
    lines: [...s.rows.filter((r) => r.to !== '' && r.amount > 0).map((r) => ({ ledgerId: r.to as number, side: 'Dr' as const, amount: r.amount })), { ledgerId: s.from as number, side: 'Cr', amount: total }],
    ...(bankSide ? { mode: s.mode, refNo: s.ref.trim(), instrDate: s.idate } : {}),
  });
  const save = useSaveVoucher('Contra', voucher, onClose);

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={<FormTitle title="Contra" no={voucher?.voucherNo ?? nextNo} chip={<ChipTag tone="b">Bank ↔ Cash</ChipTag>} />}
      footer={
        <FormFooter left={<>Total <b>{inr(total)}</b></>} version={voucher ? voucher.version + 1 : undefined}
          saveLabel="Save Contra" blocked={block.length > 0} saving={save.isPending} onClose={onClose} onSave={() => save.mutate(build())} />
      }
    >
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <DateField value={s.date} max={today} onChange={(v) => set({ date: v, ...(s.idate === s.date ? { idate: v } : {}) })} />
        <F label="Account (money out · Cr) *" className="col-span-2">
          <Select value={s.from} onChange={(e) => setFrom(Number(e.target.value))}>
            {s.from === '' && <option value="">— choose —</option>}
            {accts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Select>
        </F>
        <F label="Cur Bal"><ReadBox value={curBal(from)} /></F>
      </div>

      <Sec>Particulars</Sec>
      {s.rows.map((r, i) => (
        <div key={r.key} className="mb-2.5 rounded-xl border border-[var(--border-color)] p-2.5 sm:p-3">
          <div className="flex flex-wrap items-end gap-2.5">
            <F label="Account (money in · Dr)" className="min-w-[220px] flex-1">
              <Select value={r.to} onChange={(e) => setRow(i, { to: Number(e.target.value) })}>
                {r.to === '' && <option value="">— choose —</option>}
                {accts.filter((a) => a.id !== s.from).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </Select>
            </F>
            <F label="Amount" className="w-[150px]">
              <Input type="number" step="0.01" min={0} className="text-right font-mono" value={r.amount || ''} placeholder="0.00" onChange={(e) => setRow(i, { amount: r2(Number(e.target.value) || 0) })} />
            </F>
            {s.rows.length > 1 && <SmBtn title="Remove line" onClick={() => set({ rows: s.rows.filter((_, j) => j !== i) })}>✕</SmBtn>}
          </div>
          <div className="mt-1 text-[11px] text-[var(--text-muted)]">Cur Bal: <b className="text-[var(--text-primary)]">{curBal(tos[i])}</b></div>
        </div>
      ))}
      {s.rows.length < accts.length - 1 && <SmBtn onClick={addRow}>＋ Add account</SmBtn>}

      {bankSide && (
        <>
          <Sec>Bank Allocation</Sec>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <F label="Transaction type">
              <Select value={s.mode} onChange={(e) => set({ mode: e.target.value })}>{CONTRA_MODES.map((m) => <option key={m}>{m}</option>)}</Select>
            </F>
            <F label={`${s.mode === 'Cheque' ? 'Inst. no. (cheque)' : s.mode === 'Cash Deposit' ? 'Deposit slip no.' : 'UTR / Ref'}${s.mode === 'Cash Deposit' ? '' : ' *'}`}>
              <Input value={s.ref} maxLength={60} onChange={(e) => set({ ref: e.target.value })} />
            </F>
            <F label={`Inst. date${s.mode === 'Cheque' && s.idate > s.date ? ' · post-dated' : ''}`}><Input type="date" value={s.idate} onChange={(e) => set({ idate: e.target.value })} /></F>
            <F label="Amount"><ReadBox value={inr(total)} /></F>
          </div>
        </>
      )}

      <Msgs block={block} />
      <F label="Narration" className="mt-3">
        <Textarea rows={2} maxLength={1000} value={s.narr} placeholder={ctNarr(from, tos)} onChange={(e) => set({ narr: e.target.value })} />
      </F>
      <Sec>Posting</Sec>
      <Posting lines={post} />
    </Modal>
  );
}
